/**
 * Catalog Search — search bar for Full Catalog profiles.
 *
 * Markup lives in sections/profile-subtle.liquid (toggle button in the header
 * actions + bar/panel/backdrop below the header). The button and the whole UI
 * are only revealed when the DMO offer includes CATALOG, mirroring the shop
 * nav gating in profile-subtle.js.
 *
 * Search is served by the Storefront API `predictiveSearch` query so each
 * result includes its variant titles — products are filtered client-side to
 * the resolved country via the `_XX` variant-title suffix convention used by
 * featured-collection.liquid. The overlay never navigates and never touches
 * the URL, so `?site` and every other param stay intact.
 */
(function () {
  'use strict';

  // Tokenless Storefront API access: a hardcoded token belongs to one store
  // (prod) and is rejected with 401 on every other store (e.g. QA), while
  // tokenless works on any store's own domain. It can't read metafields, so
  // the query below sticks to native fields.
  const STOREFRONT_API_VERSION = '2024-10';

  const DEBOUNCE_MS = 300;
  const MIN_QUERY_LENGTH = 2;
  const RESULT_LIMIT = 10;
  const CACHE_MAX_ENTRIES = 50;
  const SKELETON_CARDS = 8;

  function t(key, fallback) {
    let dict = window.translations && window.translations.catalog_search;
    return dict && typeof dict[key] === 'string' ? dict[key] : fallback;
  }

  function hasCatalogOffer() {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return true;
    let po = window.data && window.data.theme && window.data.theme.productOffer;
    if (!po) return false;
    if (Array.isArray(po)) return po.some(function (o) { return String(o).toUpperCase() === 'CATALOG'; });
    if (typeof po === 'object') {
      if (po.type && String(po.type).toUpperCase() === 'CATALOG') return true;
      if (Array.isArray(po.offers)) return po.offers.some(function (o) { return o && String(o.type).toUpperCase() === 'CATALOG'; });
    }
    return false;
  }

  // Same resolution order as featured-collection.liquid: DMO targetCountry
  // (the URL can't carry PR, which is redirected to es-us), then URL locale,
  // then US.
  function resolveCountry() {
    let tc = window.data && window.data.profile && window.data.profile.targetCountry;
    if (tc) return String(tc).toUpperCase();
    let m = window.location.pathname.match(/^\/[a-z]{2}-([a-z]{2})(?=\/|$)/i);
    return m ? m[1].toUpperCase() : 'US';
  }

  function getLocalePrefix() {
    let m = window.location.pathname.match(/^\/([a-z]{2}-[a-z]{2})(?=\/|$)/i);
    return m ? '/' + m[1] : '';
  }

  // Carry the site identifier onto product links (URL first, storage
  // fallback) — same pattern as featured-collection's outbound links.
  function resolveSite() {
    let fromUrl = new URLSearchParams(window.location.search).get('site');
    if (fromUrl) return fromUrl;
    try {
      return sessionStorage.getItem('shopify_site_identifier') ||
             localStorage.getItem('shopify_site_identifier') || '';
    } catch (_e) {
      return '';
    }
  }

  function productUrl(handle) {
    let url = getLocalePrefix() + '/products/' + handle;
    let site = resolveSite();
    if (site) url += '?site=' + encodeURIComponent(site);
    return url;
  }

  // Browse-only gate, mirroring featured-collection.liquid: outside US/CA/PR,
  // product clicks send the user to the wellness assessment until the
  // completion flag is set on this device.
  const UNGATED_COUNTRIES = ['US', 'CA', 'PR'];

  function isBrowseGated(country) {
    if (typeof Shopify !== 'undefined' && Shopify.designMode) return false;
    if (UNGATED_COUNTRIES.indexOf(country) !== -1) return false;
    try {
      return localStorage.getItem('pro2col_assessment_completed') !== 'true';
    } catch (_e) {
      return true;
    }
  }

  // Same construction as featured-collection.liquid's buildFindYourAgeUrl:
  // external base + /{locale}/Intro, preserving the current query string and
  // guaranteeing `site` (URL first, storage fallback).
  function buildFindYourAgeUrl() {
    let baseUrl = (window.externalLinks && window.externalLinks.findYourAgeUrl) || '';
    if (!baseUrl) return '';
    let cleanBase = baseUrl.replace(/\/$/, '');
    let firstSegment = window.location.pathname.split('/').filter(Boolean)[0] || '';
    let locale = /^[a-z]{2}(-[a-z]{2})?$/i.test(firstSegment) ? firstSegment : '';
    let path = locale ? '/' + locale + '/Intro' : '/Intro';
    let search = new URLSearchParams(window.location.search);
    if (!search.get('site')) {
      let site = resolveSite();
      if (site) search.set('site', site);
    }
    let qs = search.toString();
    return cleanBase + path + (qs ? '?' + qs : '');
  }

  const QUERY =
    'query CatalogSearch($query: String!, $country: CountryCode!) @inContext(country: $country) {' +
    '  predictiveSearch(query: $query, limit: ' + RESULT_LIMIT + ', types: [PRODUCT], unavailableProducts: HIDE) {' +
    '    products {' +
    '      title' +
    '      handle' +
    '      productType' +
    '      featuredImage { url(transform: { maxWidth: 360, maxHeight: 360 }) altText }' +
    '      variants(first: 100) { nodes { title image { url(transform: { maxWidth: 360, maxHeight: 360 }) altText } } }' +
    '    }' +
    '  }' +
    '}';

  function storefrontApiUrl() {
    return 'https://' + window.location.hostname + '/api/' + STOREFRONT_API_VERSION + '/graphql.json';
  }

  // A product is hidden only when it HAS country-suffixed variants and none of
  // them match the resolved country. Products without any `_XX` suffix are
  // country-agnostic and stay visible (same fallback rule as
  // featured-collection.liquid's variantsForCountry).
  const COUNTRY_SUFFIX_RE = /_[A-Z]{2}(?![A-Z])/;

  // Never show Bioniq products (separate brand/store — productType "Bioniq",
  // titles contain "Bioniq") or internal tax line items ("Taxes"; same
  // word-boundary rule as src/scripts/cart-taxes-removal.ts).
  const BIONIQ_RE = /bioniq/i;
  const TAX_TITLE_RE = /\btax(?:es)?\b/i;

  function isExcludedProduct(product) {
    return (
      BIONIQ_RE.test(product.productType || '') ||
      BIONIQ_RE.test(product.title || '') ||
      TAX_TITLE_RE.test(product.title || '')
    );
  }

  function matchesCountry(product, country) {
    let nodes = (product.variants && product.variants.nodes) || [];
    let hasAnySuffix = false;
    let suffix = '_' + country;
    for (let i = 0; i < nodes.length; i++) {
      let title = String(nodes[i].title || '').toUpperCase();
      if (title.indexOf(suffix) !== -1) return true;
      if (COUNTRY_SUFFIX_RE.test(title)) hasAnySuffix = true;
    }
    return !hasAnySuffix;
  }

  // The product featuredImage is shared by every market (often another
  // country's packshot), so prefer the image of the resolved country's
  // `_XX` variant — the same variant the PDP shows.
  function productImage(product, country) {
    let nodes = (product.variants && product.variants.nodes) || [];
    let suffix = '_' + country;
    for (let i = 0; i < nodes.length; i++) {
      let title = String(nodes[i].title || '').toUpperCase();
      if (title.indexOf(suffix) !== -1 && nodes[i].image && nodes[i].image.url) return nodes[i].image;
    }
    return product.featuredImage && product.featuredImage.url ? product.featuredImage : null;
  }

  document.addEventListener('DOMContentLoaded', function () {
    let toggleBtn = document.getElementById('catalogSearchToggle');
    let searchUi = document.getElementById('catalogSearch');
    if (!toggleBtn || !searchUi) return;

    let input = searchUi.querySelector('[data-catalog-search-input]');
    let closeBtn = searchUi.querySelector('[data-catalog-search-close]');
    let panel = searchUi.querySelector('[data-catalog-search-panel]');
    let grid = searchUi.querySelector('[data-catalog-search-grid]');
    let message = searchUi.querySelector('[data-catalog-search-message]');
    let backdrop = searchUi.querySelector('[data-catalog-search-backdrop]');
    if (!input || !panel || !grid || !message) return;

    let cache = new Map(); // 'COUNTRY:query' -> products[]
    let controller = null;
    let debounceTimer = null;
    let lastRenderedKey = null;

    // ── Gating ─────────────────────────────────────────────────────────
    function syncGate() {
      let show = hasCatalogOffer();
      toggleBtn.style.display = show ? '' : 'none';
      if (!show) close();
    }
    syncGate();
    document.addEventListener('dmo:data:loaded', syncGate);

    // ── Open / close ───────────────────────────────────────────────────
    function isOpen() {
      return !searchUi.hasAttribute('hidden');
    }

    function open() {
      searchUi.removeAttribute('hidden');
      toggleBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      input.focus();
    }

    function close() {
      if (!isOpen()) return;
      searchUi.setAttribute('hidden', '');
      toggleBtn.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      if (controller) controller.abort();
      clearTimeout(debounceTimer);
    }

    toggleBtn.addEventListener('click', function () {
      if (isOpen()) close();
      else open();
    });
    if (closeBtn) closeBtn.addEventListener('click', close);
    if (backdrop) backdrop.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
    });

    // ── Rendering ──────────────────────────────────────────────────────
    function clearGrid() {
      while (grid.firstChild) grid.removeChild(grid.firstChild);
    }

    function showMessage(text) {
      clearGrid();
      panel.removeAttribute('hidden');
      message.textContent = text;
      message.removeAttribute('hidden');
    }

    function hidePanel() {
      panel.setAttribute('hidden', '');
      clearGrid();
      message.setAttribute('hidden', '');
      lastRenderedKey = null;
    }

    function showSkeleton() {
      clearGrid();
      message.setAttribute('hidden', '');
      panel.removeAttribute('hidden');
      for (let i = 0; i < SKELETON_CARDS; i++) {
        let card = document.createElement('div');
        card.className = 'catalog-search__card catalog-search__card--skeleton';
        let img = document.createElement('div');
        img.className = 'catalog-search__card-image';
        let title = document.createElement('div');
        title.className = 'catalog-search__card-title';
        card.appendChild(img);
        card.appendChild(title);
        grid.appendChild(card);
      }
    }

    let lastProducts = null;

    function renderProducts(products) {
      lastProducts = products;
      if (!products.length) {
        showMessage(t('no_results', 'No products found.'));
        return;
      }
      clearGrid();
      message.setAttribute('hidden', '');
      panel.removeAttribute('hidden');
      // Gated markets get the assessment quiz instead of the product page
      // (falls back to the product page if the external quiz URL is missing).
      let quizUrl = isBrowseGated(resolveCountry()) ? buildFindYourAgeUrl() : '';
      products.forEach(function (product) {
        let link = document.createElement('a');
        link.className = 'catalog-search__card';
        link.href = quizUrl || productUrl(product.handle);

        let imgWrap = document.createElement('div');
        imgWrap.className = 'catalog-search__card-image';
        if (product.image) {
          let img = document.createElement('img');
          img.src = product.image.url;
          img.alt = product.image.altText || product.title;
          img.loading = 'lazy';
          img.width = 180;
          img.height = 180;
          imgWrap.appendChild(img);
        }

        let title = document.createElement('span');
        title.className = 'catalog-search__card-title';
        title.textContent = product.title;

        link.appendChild(imgWrap);
        link.appendChild(title);
        grid.appendChild(link);
      });
    }

    // ── Fetching ───────────────────────────────────────────────────────
    function fetchResults(query, country) {
      let key = country + ':' + query.toLowerCase();
      if (cache.has(key)) {
        lastRenderedKey = key;
        renderProducts(cache.get(key));
        return;
      }

      if (controller) controller.abort();
      controller = new AbortController();
      lastRenderedKey = key;
      showSkeleton();

      fetch(storefrontApiUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: QUERY, variables: { query: query, country: country } }),
        signal: controller.signal,
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Storefront API request failed: ' + res.status);
          return res.json();
        })
        .then(function (json) {
          if (json.errors) throw new Error('GraphQL errors: ' + JSON.stringify(json.errors));
          let products = ((json.data && json.data.predictiveSearch && json.data.predictiveSearch.products) || [])
            .filter(function (p) { return !isExcludedProduct(p) && matchesCountry(p, country); })
            .map(function (p) { p.image = productImage(p, country); return p; });

          if (cache.size >= CACHE_MAX_ENTRIES) {
            cache.delete(cache.keys().next().value);
          }
          cache.set(key, products);
          if (lastRenderedKey === key) renderProducts(products);
        })
        .catch(function (err) {
          if (err && err.name === 'AbortError') return;
          console.error('[Catalog Search]', err);
          if (lastRenderedKey === key) {
            showMessage(t('error', 'Something went wrong. Please try again.'));
          }
        });
    }

    // Re-point rendered links when the assessment gate unlocks: completed on
    // this page load, restored from bfcache, or completed in another tab
    // (same triggers featured-collection.liquid listens to).
    function refreshGate() {
      if (lastProducts && !panel.hasAttribute('hidden')) renderProducts(lastProducts);
    }
    document.addEventListener('pro2col:assessment-completed', refreshGate);
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) refreshGate();
    });
    window.addEventListener('storage', function (event) {
      if (event.key === 'pro2col_assessment_completed') refreshGate();
    });

    input.addEventListener('input', function () {
      clearTimeout(debounceTimer);
      let query = input.value.trim();
      if (query.length < MIN_QUERY_LENGTH) {
        if (controller) controller.abort();
        hidePanel();
        return;
      }
      debounceTimer = setTimeout(function () {
        fetchResults(query, resolveCountry());
      }, DEBOUNCE_MS);
    });

    // Enter submits immediately (skips the debounce wait).
    input.addEventListener('keydown', function (e) {
      if (e.key !== 'Enter') return;
      e.preventDefault();
      clearTimeout(debounceTimer);
      let query = input.value.trim();
      if (query.length >= MIN_QUERY_LENGTH) fetchResults(query, resolveCountry());
    });
  });
})();
